import { useMutation } from "@tanstack/react-query";
import { Trans, useLingui } from "@lingui/react/macro";
import { CircleAlert, Info, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { forgotPassword } from "@/lib/auth";
import { useAuth } from "@/hooks/useAuth";
import { chipToast } from "@/components/ui/chip-toast";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const SSO_LABELS: Record<string, string> = {
  google: "Google",
  facebook: "Facebook",
  apple: "Apple",
  amazon: "Amazon",
};

export function ChangePasswordDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useLingui();
  const { user } = useAuth();
  const email = user?.email ?? "";

  // Federated (SSO) accounts have no Cognito password, so a reset email can't
  // be sent — their password is managed by the identity provider.
  const ssoProvider =
    user?.authProvider && user.authProvider !== "cognito"
      ? user.authProvider
      : null;
  const ssoLabel = ssoProvider
    ? (SSO_LABELS[ssoProvider] ?? t`your identity provider`)
    : null;

  const sendMutation = useMutation({
    mutationFn: () => forgotPassword({ email }),
    onSuccess: () => {
      chipToast({ message: t`Password reset link sent to ${email}.` });
      onOpenChange(false);
    },
    onError: (error: unknown) => {
      toast.error(
        error instanceof Error ? error.message : t`Failed to send reset link`,
      );
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-175">
        <DialogHeader>
          <DialogTitle><Trans>Change password</Trans></DialogTitle>
          <DialogDescription>
            <Trans>Update your password to keep your account secure.</Trans>
          </DialogDescription>
        </DialogHeader>

        {ssoLabel ? (
          <div className="flex items-start gap-3 rounded-lg bg-(--color-surface-muted) p-4">
            <div className="flex size-12 shrink-0 items-center justify-center rounded-lg bg-(--color-surface-accent) text-(--color-text-secondary)">
              <ShieldCheck className="size-6" />
            </div>
            <div>
              <p className="text-sm font-medium text-(--color-text-primary)">
                <Trans>Single sign-on enabled</Trans>
              </p>
              <p className="text-sm text-(--color-text-secondary)">
                <Trans>
                  Your password is managed by {ssoLabel}. To change it, update
                  your password in your {ssoLabel} account.
                </Trans>
              </p>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-5">
            <div className="flex flex-col gap-2">
              <span className="text-xs font-medium text-(--color-text-primary)">
                <Trans>Password</Trans>
              </span>
              <div className="flex h-11 items-center rounded-lg border border-(--color-border) bg-(--color-surface-muted) px-3 text-sm tracking-widest text-(--color-text-primary)">
                ••••••••••••••
              </div>
              <div className="flex items-center gap-2 text-sm text-(--color-text-secondary)">
                <Info className="size-4" />
                <span>
                  <Trans>
                    Last changed{" "}
                    <span className="font-medium text-(--color-text-primary)">
                      45 days
                    </span>{" "}
                    ago.
                  </Trans>
                </span>
              </div>
            </div>

            <div className="flex items-start gap-3 rounded-lg bg-(--color-warning-bg) p-3">
              <div className="flex size-12 shrink-0 items-center justify-center rounded-lg bg-(--color-warning) text-white">
                <CircleAlert className="size-6" />
              </div>
              <div>
                <p className="text-sm font-medium text-(--color-warning-text)">
                  <Trans>Please note</Trans>
                </p>
                <p className="text-sm text-(--color-text-secondary)">
                  <Trans>
                    After changing your password, all other active sessions will
                    be signed out. This device will remain signed in.
                  </Trans>
                </p>
              </div>
            </div>
          </div>
        )}

        <DialogFooter className="gap-2">
          {ssoLabel ? (
            <Button type="button" onClick={() => onOpenChange(false)}>
              <Trans>Got it</Trans>
            </Button>
          ) : (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={sendMutation.isPending}
              >
                <Trans>Cancel</Trans>
              </Button>
              <Button
                type="button"
                onClick={() => sendMutation.mutate()}
                disabled={sendMutation.isPending || !email}
              >
                {sendMutation.isPending ? t`Sending…` : t`Send reset link`}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
